import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, Loader2, LogOut, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  accountGateView,
  DIBS_CONTACT_URL,
  readPendingPayment,
} from "@/lib/accountAccess";
import { complimentaryTrialHasEnded } from "@/lib/trialAccess";
import { startPlanPayment } from "./startPlanPayment";

const CONFIRM_ATTEMPTS = 15;
const CONFIRM_INTERVAL_MS = 2_000;

interface AccountAccessGateProps {
  // The dashboard reads the subscription separately from the auth context.
  onRefreshSubscription?: () => Promise<void>;
}

/**
 * What an owner sees instead of the dashboard until a plan or trial is in place.
 * The dashboard opens by itself once stripe-webhook records the payment: the auth
 * context and subscription refresh below are what lift this screen.
 */
export default function AccountAccessGate({ onRefreshSubscription }: AccountAccessGateProps) {
  const { user, signOut, refreshBusinessContext } = useAuth();
  const checkoutResult = useMemo(
    () => new URLSearchParams(window.location.search).get("checkout"),
    [],
  );
  const [confirmTimedOut, setConfirmTimedOut] = useState(false);
  const [paying, setPaying] = useState(false);

  const { data: business, isLoading } = useQuery({
    queryKey: ["account-access-gate", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("businesses")
        .select("subscription_status, stripe_customer_id, next_billing_date, temporary_metadata")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (checkoutResult !== "success") return;

    let cancelled = false;
    let attempts = 0;
    let timeoutId: number | null = null;
    const refreshUntilWebhookSettles = async () => {
      if (cancelled) return;
      attempts += 1;
      await Promise.all([refreshBusinessContext(), onRefreshSubscription?.()]);
      if (cancelled) return;
      if (attempts < CONFIRM_ATTEMPTS) {
        timeoutId = window.setTimeout(refreshUntilWebhookSettles, CONFIRM_INTERVAL_MS);
      } else {
        setConfirmTimedOut(true);
      }
    };

    void refreshUntilWebhookSettles();
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [checkoutResult, onRefreshSubscription, refreshBusinessContext]);

  const pay = async () => {
    setPaying(true);
    try {
      const opened = await startPlanPayment("onboarding");
      if (!opened) {
        await Promise.all([refreshBusinessContext(), onRefreshSubscription?.()]);
        setPaying(false);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not open checkout. Please try again.");
      setPaying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading your account" />
      </div>
    );
  }

  const pending = readPendingPayment(business?.temporary_metadata);
  const trialEnded = complimentaryTrialHasEnded({
    subscriptionStatus: business?.subscription_status ?? null,
    stripeCustomerId: business?.stripe_customer_id ?? null,
    nextBillingDate: business?.next_billing_date ?? null,
  });
  const view = accountGateView({
    confirmingPayment: checkoutResult === "success",
    pending,
    trialEnded,
  });

  const title =
    view === "confirming"
      ? "Confirming your payment"
      : trialEnded
        ? "Your trial has ended"
        : view === "pay"
          ? "Finish setting up Dibs"
          : "Your account is almost ready";

  const description =
    view === "confirming"
      ? confirmTimedOut
        ? "This is taking longer than usual. Your payment is safe, so check again in a minute."
        : "Your dashboard opens as soon as Stripe confirms your payment."
      : view === "pay"
        ? trialEnded
          ? "Dibs has stopped replying to your customers, but their messages are still saved. Pay for your plan to pick up where you left off."
          : "Your plan is ready. Your dashboard opens as soon as your payment goes through."
        : view === "trial_ended"
          ? "Dibs has stopped replying to your customers, but their messages are still saved. Talk to your Dibs contact to keep going."
          : "Your Dibs contact will send your plan shortly. Not working with us yet? Message us to get set up.";

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-background p-4"
      data-testid="account-access-gate"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center sm:p-8">
        <img
          src="/dibs_assets/red_logo_with_transparent_bg.png"
          alt="Dibs"
          className="mx-auto h-10 w-10 object-contain"
        />
        <h1 className="mt-4 font-heading text-xl font-semibold text-card-foreground">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>

        {view === "pay" && pending?.summary && (
          <p className="mt-5 rounded-lg bg-muted/60 px-4 py-3 text-sm font-medium text-foreground">
            {pending.summary}
          </p>
        )}
        {view === "pay" && checkoutResult === "cancel" && (
          <p className="mt-3 text-sm text-muted-foreground">
            Payment wasn't completed. You can try again whenever you're ready.
          </p>
        )}

        <div className="mt-6 flex flex-col items-center gap-2">
          {view === "confirming" && !confirmTimedOut && (
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Confirming your payment" />
          )}
          {view === "confirming" && confirmTimedOut && (
            <Button size="sm" className="standard-action-button" onClick={() => window.location.reload()}>
              Check again
            </Button>
          )}
          {view === "pay" && (
            <Button size="sm" className="standard-action-button" onClick={() => void pay()} disabled={paying}>
              {paying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />}
              Pay now
            </Button>
          )}
          {(view === "trial_ended" || view === "waiting_for_plan") && (
            <Button asChild size="sm" className="standard-action-button">
              <a href={DIBS_CONTACT_URL} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                Message Dibs
              </a>
            </Button>
          )}
          <Button type="button" variant="ghost" size="sm" onClick={() => void signOut()}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            Sign out
          </Button>
        </div>

        {view === "pay" && (
          <p className="mt-5 text-xs text-muted-foreground">
            Secure checkout powered by Stripe.
            {pending?.testMode && " Test mode is active; no real money will be charged."}
          </p>
        )}
      </div>
    </main>
  );
}
